import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { requestsService } from "../services/requestsService";
import { ReactComponent as TrashIcon } from "../assets/icons/trash.svg";

interface AddLinkModalProps {
  onClose: () => void;
  onSuccess?: () => void;
}

interface LinkInput {
  url: string;
}

const AddLinkModal: React.FC<AddLinkModalProps> = ({ onClose, onSuccess }) => {
  const navigate = useNavigate();
  const [links, setLinks] = useState<LinkInput[]>([{ url: "" }]);
  const [context, setContext] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const addNewLink = () => {
    setLinks([...links, { url: "" }]);
  };

  const removeLink = (index: number) => {
    if (links.length > 1) {
      const newLinks = links.filter((_, i) => i !== index);
      setLinks(newLinks);
    }
  };

  const updateLink = (index: number, value: string) => {
    const newLinks = [...links];
    newLinks[index].url = value;
    setLinks(newLinks);
    
    // Clear error when user starts typing
    if (error) {
      setError(null);
    }
  };

  const isValidUrl = (url: string): boolean => {
    try {
      // Auto-prepend https:// if no protocol is present
      const normalizedUrl = url.match(/^https?:\/\//) ? url : `https://${url}`;
      const parsed = new URL(normalizedUrl);
      return parsed.hostname.includes('.') || parsed.hostname === 'localhost';
    } catch {
      return false;
    }
  };

  const handleSubmit = async () => {
    // Filter out empty links
    const validLinks = links.filter(link => link.url.trim() !== "");
    
    if (validLinks.length === 0) {
      setError("Please add at least one link");
      return;
    }

    // Client-side URL validation
    const invalidLinks = validLinks.filter(link => !isValidUrl(link.url));
    if (invalidLinks.length > 0) {
      setError(`Invalid URL(s): ${invalidLinks.map(link => link.url).join(', ')}`);
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // Create a single request with multiple links
      const linkUrls = validLinks.map(link => link.url);
      const createdRequest = await requestsService.createRequest({
        request_type: 'link',
        links: linkUrls,
        context: context || undefined
      });

      if (onSuccess) {
        onSuccess();
      }
      
      // Navigate to the created request
      onClose();
      navigate(`/requests/view/${createdRequest.id}`);
    } catch (error: any) {
      console.error("Failed to create link request:", error);
      
      // Check if it's a validation error with specific messages
      if (error.response?.status === 422 && error.response?.data?.errors) {
        const errorMessages = error.response.data.errors;
        setError(errorMessages.join('. '));
      } else {
        setError("Failed to create request. Please try again.");
      }
      setIsSubmitting(false);
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div 
      className="modal-backdrop" 
      onClick={handleBackdropClick}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000
      }}
    >
      <div 
        className="modal-content"
        style={{
          backgroundColor: "white",
          borderRadius: "12px",
          maxWidth: "600px",
          width: "90%",
          maxHeight: "80vh",
          overflow: "auto",
          boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
        }}
      >
        {/* Header */}
        <div style={{
          padding: "24px 24px 20px 24px",
          borderBottom: "1px solid #e5e7eb",
          position: "relative"
        }}>
          <button
            onClick={onClose}
            style={{
              position: "absolute",
              top: "24px",
              right: "24px",
              background: "none",
              border: "none",
              color: "#6b7280",
              fontSize: "14px",
              textDecoration: "underline",
              cursor: "pointer",
              padding: 0
            }}
          >
            Cancel
          </button>
          <h2 style={{ margin: 0, fontSize: "20px", fontWeight: 600 }}>
            Add Links
          </h2>
          <p style={{ margin: "8px 0 0 0", fontSize: "14px", color: "#6b7280" }}>
            Add one or more links you want us to incorporate into your content
          </p>
        </div>

        {/* Body */}
        <div style={{ padding: "24px" }}>
          {/* Links Section */}
          <div style={{ marginBottom: "24px" }}>
            <div style={{ 
              display: "flex", 
              justifyContent: "space-between", 
              alignItems: "center",
              marginBottom: "12px"
            }}>
              <label style={{ 
                fontSize: "14px", 
                fontWeight: 500, 
                color: "#374151" 
              }}>
                Links
              </label>
              <button
                onClick={addNewLink}
                className="tertiary-info-button"
                style={{ 
                  padding: "6px 12px",
                  fontSize: "14px",
                    cursor:  "pointer"
                }}
              >
                Add Another Link
              </button>
            </div>

            <div style={{ 
              display: "flex", 
              flexDirection: "column", 
              gap: "12px" 
            }}>
              {links.map((link, index) => (
                <div 
                  key={index}
                  style={{ 
                    display: "flex", 
                    gap: "8px",
                    alignItems: "center"
                  }}
                >
                  <input
                    type="url"
                    value={link.url}
                    onChange={(e) => updateLink(index, e.target.value)}
                    placeholder="https://example.com/event"
                    style={{
                      flex: 1,
                      padding: "8px 12px",
                      border: `1px solid ${
                        link.url && !isValidUrl(link.url) ? "#ef4444" : "#d1d5db"
                      }`,
                      borderRadius: "6px",
                      fontSize: "14px",
                      outline: "none",
                      transition: "border-color 0.15s"
                    }}
                    onFocus={(e) => {
                      if (!(link.url && !isValidUrl(link.url))) {
                        e.target.style.borderColor = "#6366f1";
                      }
                    }}
                    onBlur={(e) => {
                      if (!(link.url && !isValidUrl(link.url))) {
                        e.target.style.borderColor = "#d1d5db";
                      }
                    }}
                  />
                  {links.length > 1 && (
                    <button
                      onClick={() => removeLink(index)}
                      style={{
                        padding: "8px",
                        backgroundColor: "transparent",
                        border: "1px solid #e5e7eb",
                        borderRadius: "6px",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        transition: "all 0.15s"
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = "#fee2e2";
                        e.currentTarget.style.borderColor = "#fca5a5";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "transparent";
                        e.currentTarget.style.borderColor = "#e5e7eb";
                      }}
                    >
                      <TrashIcon style={{ width: "16px", height: "16px", color: "#ef4444" }} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Context Section */}
          <div style={{ marginTop: "24px" }}>
            <label 
              htmlFor="context"
              style={{ 
                display: "block",
                fontSize: "14px", 
                fontWeight: 500, 
                color: "#374151",
                marginBottom: "8px"
              }}
            >
              Context (Optional)
            </label>
            <textarea
              id="context"
              value={context}
              onChange={(e) => setContext(e.target.value)}
              placeholder="Add any additional context about these links..."
              style={{
                width: "90%",
                minHeight: "100px",
                padding: "8px 12px",
                border: "1px solid #d1d5db",
                borderRadius: "6px",
                fontSize: "14px",
                resize: "vertical",
                outline: "none",
                transition: "border-color 0.15s"
              }}
              onFocus={(e) => {
                e.target.style.borderColor = "#6366f1";
              }}
              onBlur={(e) => {
                e.target.style.borderColor = "#d1d5db";
              }}
            />
          </div>

          {/* Error Message */}
          {error && (
            <div style={{
              marginTop: "16px",
              padding: "12px",
              backgroundColor: "#fee2e2",
              border: "1px solid #fca5a5",
              borderRadius: "6px",
              color: "#dc2626",
              fontSize: "14px"
            }}>
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: "20px 24px",
          borderTop: "1px solid #e5e7eb",
          display: "flex",
          justifyContent: "flex-end",
          gap: "12px"
        }}>
          <button
            onClick={handleSubmit}
            className="primary-button"
            disabled={isSubmitting}
            style={{
              opacity: isSubmitting ? 0.6 : 1,
              cursor: isSubmitting ? "not-allowed" : "pointer"
            }}
          >
            {isSubmitting ? "Creating..." : "Create Request"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddLinkModal;